var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import set from "lodash.set";
import get from "lodash.get";
import typeSchema from "@readcube/rcp-meta/type_schema.json";
import { parseAuthors } from "@readcube/parse-authors-string";
import { parseError } from "./errors";
import { warnOrErrorAsPrettyError } from "./logging";
// Our master mapping schema may map same bib type to multiple rc types such as:
//
//   bib type     ->    rc type
//   ---------------------------------------
//   misc               dictionary_entry
//   misc               encyclopedia_article
//   misc               document
//
// When importing bib we need to choose one of the types. The overrides below define what
// mapping to choose:
const multiBibTypeToRcTypeOverrides = {
    'misc': 'document',
    'article': 'article',
};
const bibTypeRcTypeMap = Object
    .entries(typeSchema.types)
    .reduce((acc, entry) => {
    const [rcTypeName, rcTypeDef] = entry;
    if (!rcTypeDef.bib_type)
        return acc;
    if (multiBibTypeToRcTypeOverrides[rcTypeDef.bib_type]) {
        acc[rcTypeDef.bib_type] = multiBibTypeToRcTypeOverrides[rcTypeDef.bib_type];
        return acc;
    }
    if (acc[rcTypeDef.bib_type])
        console.log(`
        Multiple rc type mappins found for bib type: ${rcTypeDef.bib_type}.

        To resolve this either edit the master mapping schema or add type mapping override in bib parser.
        `);
    acc[rcTypeDef.bib_type] = rcTypeName;
    return acc;
}, {});
const cleanDoi = (doi) => doi.replace(/https?:\/\/(dx.)?doi.org\//, '');
const getBisTagRc = (rcTypeName) => {
    const fieldNames = typeSchema
        .types[rcTypeName]
        .fields;
    const bibTagRc = fieldNames
        .reduce((acc, fieldName) => {
        const fieldDef = typeSchema
            .field_defs[fieldName];
        if (fieldDef && ("bib" in fieldDef)) {
            let bibTags = [fieldDef.bib];
            if (fieldDef.bib.includes(',')) {
                const multiBibTags = fieldDef
                    .bib
                    .split(',')
                    .map(r => r.trim())
                    .filter(r => r);
                if (multiBibTags === null || multiBibTags === void 0 ? void 0 : multiBibTags.length)
                    bibTags = multiBibTags;
            }
            if ("readcube" in fieldDef)
                bibTags.forEach(tag => {
                    if (!acc[tag])
                        acc[tag] = [];
                    acc[tag].push({ path: fieldDef.readcube, isArray: fieldDef.input.array });
                });
            if ("custom_metadata" in fieldDef)
                bibTags.forEach(tag => {
                    if (!acc[tag])
                        acc[tag] = [];
                    acc[tag].push({
                        path: 'custom_metadata.' + fieldDef.custom_metadata,
                        isArray: fieldDef.input.array,
                    });
                });
        }
        return acc;
    }, {});
    return bibTagRc;
};
export const bibEntryToRcItem = (bibEntry, text, errors, prettyErrors, hash) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const entryText = text.slice(bibEntry.startLocation, bibEntry.endLocation);
    const originalId = yield hash(entryText);
    let rcItem = {
        import_data: {
            original_id: originalId
        }
    };
    let bibType = bibEntry
        .type;
    const rcItemType = (bibType && bibTypeRcTypeMap[bibType]) || "article";
    set(rcItem, "item_type", rcItemType);
    if (bibEntry.citationKey)
        set(rcItem, "user_data.citekey", bibEntry.citationKey);
    const bibTagRc = getBisTagRc(rcItemType);
    bibEntry
        .tags
        .forEach(tag => {
        var _a, _b, _c, _d;
        if (tag.fieldName === 'file') {
            if (!tag.fieldValue)
                return;
            const zoteroFilePathMatches = Array.from((_a = tag
                .fieldValue) === null || _a === void 0 ? void 0 : _a.matchAll(/.*?(pdf|full text):(?<zoteroFilePath>.*?):application\/pdf/ig));
            const importPaths = zoteroFilePathMatches
                .map(match => { var _a; return (_a = match === null || match === void 0 ? void 0 : match.groups) === null || _a === void 0 ? void 0 : _a.zoteroFilePath; })
                .filter(f => f)
                .map(zoteroFilePath => ({ import_path: zoteroFilePath }));
            let files = get(rcItem, 'files', []);
            if (importPaths === null || importPaths === void 0 ? void 0 : importPaths.length) {
                files = [...files, ...importPaths];
            }
            else {
                files.push({ import_path: tag.fieldValue });
            }
            set(rcItem, 'files', files);
        }
        const rcs = bibTagRc[tag.fieldName];
        if (!rcs || !rcs.length)
            return;
        for (const rc of rcs) {
            if (rc.path) {
                if (rc.isArray) {
                    setArrayRcValue(rc, tag, rcItem);
                    continue;
                }
                let val = tag.fieldValue;
                if (rc.path === 'ext_ids.doi')
                    val = cleanDoi(tag.fieldValue);
                if (rc.path === 'ext_ids.pmid') {
                    const pmidVal = tag.fieldValue;
                    const validPmid = pmidVal.match(/^\d+$/i);
                    if (!validPmid) {
                        const pmidError = parseError(`Invalid value for PMID: ${tag.fieldValue}`, tag.startLocation);
                        errors.push(pmidError);
                        prettyErrors.push(warnOrErrorAsPrettyError(text, pmidError));
                        return;
                    }
                }
                if (rc.path === 'ext_ids.pmcid') {
                    const pmcidVal = tag.fieldValue;
                    const validPmcid = pmcidVal.match(/^(?:PMC)?\d+$/i);
                    if (!validPmcid) {
                        const pmcidError = parseError(`Invalid value for PMCID: ${tag.fieldValue}`, tag.startLocation);
                        errors.push(pmcidError);
                        prettyErrors.push(warnOrErrorAsPrettyError(text, pmcidError));
                        return;
                    }
                }
                if (rc.path === 'article.pagination')
                    val = val === null || val === void 0 ? void 0 : val.replace('--', '-');
                if (rc.path === 'custom_metadata.date') {
                    if (((_b = rcItem === null || rcItem === void 0 ? void 0 : rcItem.article) === null || _b === void 0 ? void 0 : _b.year) == null) {
                        const year = /\d{1,4}/.exec(val.trim());
                        if (year)
                            set(rcItem, 'article.year', val);
                    }
                    const monthTag = bibEntry
                        .tags
                        .find(t => t.fieldName === 'month');
                    const monthValue = (_c = monthTag === null || monthTag === void 0 ? void 0 : monthTag.fieldValue) === null || _c === void 0 ? void 0 : _c.trim();
                    const monthValueMatch = (_d = monthValue === null || monthValue === void 0 ? void 0 : monthValue.match(/^\d{1,2}/)) === null || _d === void 0 ? void 0 : _d[0];
                    if (monthValueMatch)
                        val = val.trim() + '-' + monthValueMatch;
                }
                set(rcItem, rc.path, val.trim());
            }
        }
    });
    const hasTitleOrExtIds = ((_a = rcItem === null || rcItem === void 0 ? void 0 : rcItem.article) === null || _a === void 0 ? void 0 : _a.title) != null
        || (rcItem.ext_ids
            && Object.values(rcItem.ext_ids).filter(v => v).length);
    if (!hasTitleOrExtIds) {
        const error = parseError('Cannot convert. Bib entry does not contain enough data. Ensure that entry has a title or doi present.', bibEntry.startLocation);
        errors.push(error);
        const prettyError = warnOrErrorAsPrettyError(text, error);
        prettyErrors.push(prettyError);
        return;
    }
    return rcItem;
});
const setArrayRcValue = (rc, t, rcItem) => {
    if (rc.path === 'article.authors') {
        let authors = [];
        // test for string formatted as: "Last, First" or "Last, First Middle"
        const SINGLE_BIB_FORMATTED_AUTHOR_RGX = /^([^\s,]+),\s*([\w]+(?:\s[\w]+)?)$/;
        const isBibFormattedAuthor = SINGLE_BIB_FORMATTED_AUTHOR_RGX.test(t.fieldValue.trim())
            || t.fieldValue.includes(' and ');
        if (isBibFormattedAuthor) {
            authors =
                t.fieldValue
                    .replace(/\n/g, '')
                    .split(" and ")
                    .map(author => {
                    var _a;
                    author = author.trim();
                    if (author.includes(',')) {
                        const [last, first] = author.split(',');
                        author = first.trim() + " " + last.trim();
                    }
                    const parsedAuthor = ((_a = parseAuthors(author)) === null || _a === void 0 ? void 0 : _a[0]) || '';
                    return parsedAuthor;
                });
        }
        else {
            authors = parseAuthors(t.fieldValue);
        }
        const valArray = get(rcItem, rc.path, []);
        set(rcItem, rc.path, [...valArray, ...authors]);
        return;
    }
    if (rc.path === 'user_data.tags') {
        let tags = t.fieldValue
            .split(",")
            .filter(t => t)
            .map(t => t.trim());
        const valArray = get(rcItem, rc.path, []);
        set(rcItem, rc.path, [...valArray, ...tags]);
        return;
    }
    const valArray = get(rcItem, rc.path, []);
    valArray.push(t.fieldValue.trim());
    set(rcItem, rc.path, valArray);
};
